// NullRenderer (`?render=none`): no canvas; sim + DOM + input still run through the layout maths. It drives
// the game loop from its own requestAnimationFrame (the FactoryScene's `update` does it with Phaser) and
// keeps the diagnostics honest: no views, so every count is 0 and nothing has a position.
import type { Cell, Layout } from '../layout/computeLayout.ts';
import type { ViewportInfo } from '../platform/viewport.ts';
import {
  emptySpikeReport,
  type InputOverlay,
  type RenderDebug,
  type Renderer,
  type SpikeReport,
} from './Renderer.ts';

function nextFrame(): Promise<void> {
  return new Promise((r) => requestAnimationFrame(() => r()));
}

export function createNullRenderer(): Renderer {
  let vp: ViewportInfo | null = null;
  let hl: Cell | null = null;
  let raf = 0;
  let onFrame: ((now: number) => void) | null = null;
  let received = 0;
  /** Low quality (M7): drive at most this many frames per second (0 = every animation frame). */
  let minGapMs = 0;
  let last = Number.NEGATIVE_INFINITY;
  const loop = (now: number): void => {
    raf = requestAnimationFrame(loop);
    if (minGapMs > 0 && now - last < minGapMs - 1) return;
    last = now;
    onFrame?.(now);
  };
  const debug: RenderDebug = {
    count: () => 0,
    debugPos: () => null,
    freezeAt: () => undefined,
    async idle() {
      await nextFrame();
      await nextFrame();
    },
    syncMs: () => [],
    frames: () => 0,
    eventsReceived: () => received,
  };
  return {
    kind: 'none',
    bind() {
      // nothing to draw
    },
    async init(_layout: Layout, v: ViewportInfo) {
      vp = v;
    },
    relayout(_layout: Layout, v: ViewportInfo) {
      vp = v;
    },
    drive(cb) {
      onFrame = cb;
      if (raf === 0) raf = requestAnimationFrame(loop);
    },
    frame() {
      // no views
    },
    onEvents(evs) {
      received += evs.length; // no views; counted for the silent-advance spy
    },
    rebuildFromState() {
      // no views
    },
    setHighlight(cell) {
      hl = cell;
    },
    setInputOverlay(_o: InputOverlay) {
      // nothing to draw (the router's __gm.input() reports the glows)
    },
    highlighted() {
      return hl;
    },
    async pixelAt() {
      return null;
    },
    async spike(): Promise<SpikeReport> {
      const v: ViewportInfo = vp ?? {
        vw: 0,
        vh: 0,
        iw: 0,
        ih: 0,
        insets: { top: 0, right: 0, bottom: 0, left: 0 },
        dpr: 1,
        dprEff: 1,
      };
      return emptySpikeReport('none', v, 'render=none: Phaser not started');
    },
    canvas() {
      return null;
    },
    setFpsLimit(fps) {
      minGapMs = fps > 0 ? 1000 / fps : 0;
    },
    debug,
    destroy() {
      if (raf !== 0) cancelAnimationFrame(raf);
      raf = 0;
      onFrame = null;
      hl = null;
    },
  };
}
